import { MenuSectionType } from 'src/gql/graphql';

/**
 * The type list for the consolidated Product Carousel block.
 *
 * Values are the platform's existing `MenuSectionType` rather than a parallel
 * CMS-only vocabulary, so a Puck-authored carousel and a Customize-authored menu
 * section describe themselves the same way. `IMAGE` is deliberately absent: it is
 * an image banner rather than a product carousel, and already has its own block.
 *
 * Every type is declared here from the outset even though the renderers land one
 * ticket at a time (ECOMM-12683 onwards). Fixing the whole enum up front means no
 * later change has to migrate a stored `carouselType`, which is the one thing that
 * would be genuinely hard to get right — Puck does not merge `defaultProps` in the
 * published Render path, so a stored value is all a live page has to go on.
 */
export type ProductCarouselType = Exclude<MenuSectionType, MenuSectionType.image>;

/**
 * `carouselType` and `showHeading` are typed as plain strings rather than narrowed
 * unions because Puck derives a field's type from the prop it is bound to, and the
 * shared `selectField` widget produces a `CustomField<string>`. It is also the more
 * honest type: saved page data is JSON that an AI edit or a hand-edit can author, so
 * an arbitrary string really can arrive here.
 */
export type ProductCarouselProps = {
  carouselType: string;
  heading: string;
  showHeading: string;
};

type ProductCarouselTypeDefinition = {
  /** Author-facing name in the Type dropdown. */
  label: string;
  /**
   * Heading used when the author leaves the Heading field empty.
   *
   * Takes the whole prop bag so that the types whose heading depends on another
   * field — "Popular {category}", the chosen brand's name, the chosen collection's
   * name — can derive it once those fields exist. Omitted where the heading can only
   * come from a field the type does not have yet, in which case a blank Heading
   * simply produces no heading.
   */
  autoHeading?: (props: ProductCarouselProps) => string;
};

/**
 * Insertion order is the order authors see in the Type dropdown, and follows the
 * order the types were specced in. `PRODUCT_CAROUSEL_TYPE_OPTIONS` derives from it,
 * and a unit test pins the resulting list so the ordering cannot drift silently.
 *
 * The static `autoHeading` strings match the titles the underlying storefront
 * carousels already hardcode, so an author who leaves Heading blank gets the
 * heading the storefront renders today.
 */
export const PRODUCT_CAROUSEL_TYPES: Readonly<Record<ProductCarouselType, ProductCarouselTypeDefinition>> = {
  [MenuSectionType.category]: { label: 'Popular Category' },
  [MenuSectionType.subcategory]: { label: 'Popular Subcategory' },
  [MenuSectionType.recentlyViewed]: { label: 'Recently Viewed', autoHeading: () => 'Recently Viewed' },
  [MenuSectionType.specials]: { label: 'Items on Sale', autoHeading: () => 'Items on Sale' },
  [MenuSectionType.topSellers]: { label: 'Top Sellers', autoHeading: () => 'Top Sellers' },
  [MenuSectionType.staffPicks]: { label: 'Staff Picks', autoHeading: () => 'Staff Picks' },
  [MenuSectionType.offers]: { label: 'Offers', autoHeading: () => 'Offers' },
  [MenuSectionType.brand]: { label: 'Brand' },
  [MenuSectionType.collection]: { label: 'Collection' },
  [MenuSectionType.custom]: { label: 'Custom' },
};

/**
 * The empty option. A carousel's type drives every other decision the block makes,
 * so a freshly dropped block asks the author to choose rather than silently picking
 * a type on their behalf.
 */
export const BLANK_CAROUSEL_TYPE_OPTION = { label: 'Select a type…', value: '' };

export const PRODUCT_CAROUSEL_TYPE_OPTIONS = [
  BLANK_CAROUSEL_TYPE_OPTION,
  ...Object.entries(PRODUCT_CAROUSEL_TYPES).map(([value, { label }]) => ({ label, value })),
];

/**
 * Look up a stored `carouselType`, or `undefined` when it is blank or unrecognised.
 *
 * The own-property guard is load-bearing rather than defensive: without it a stored
 * value of `constructor` or `toString` would resolve to something off
 * `Object.prototype` instead of missing. Same rationale as `interpolate`'s guard.
 */
export function carouselTypeDefinition(carouselType: string): ProductCarouselTypeDefinition | undefined {
  return Object.prototype.hasOwnProperty.call(PRODUCT_CAROUSEL_TYPES, carouselType)
    ? PRODUCT_CAROUSEL_TYPES[carouselType as ProductCarouselType]
    : undefined;
}

/**
 * The heading to render, or `undefined` when no heading should render at all.
 *
 * A blank heading and a hidden heading are deliberately different states. Blank
 * falls back to the type's generated heading, which is what makes the Heading field
 * feel pre-populated without actually writing generated text into saved page data —
 * so a carousel whose type later changes re-derives its heading instead of keeping a
 * stale one. Suppressing the heading entirely is the separate `showHeading` toggle,
 * because a carousel with no heading is a real layout choice and not just an empty
 * string.
 */
export function resolveCarouselHeading(props: ProductCarouselProps): string | undefined {
  if (props.showHeading === 'false') {
    return undefined;
  }
  // `String(...)` rather than `.trim()` directly: these props are typed, but the value
  // that actually arrives is whatever is in the saved page JSON, which an AI plugin or
  // a hand-edit can author. A stored `heading: 42` would otherwise throw here, and the
  // published render path has no error boundary — one malformed block would take down
  // the whole storefront page rather than just itself.
  // The declared type says this is always a string; the runtime value is whatever the
  // saved JSON holds, which is the whole point of the coercion above.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const authored = String(props.heading ?? '').trim();
  if (authored) {
    return authored;
  }
  return carouselTypeDefinition(props.carouselType)?.autoHeading?.(props);
}
